import React, { useEffect, useRef, useState } from "react";

/**
 * TerminalQuote — wraps a wisdom snippet in a terminal-screen frame
 * with a typewriter effect that fires once the element scrolls into view.
 * A blinking cursor follows the last character, and a subtle CRT scanline
 * overlay reinforces the retrotech feel.
 *
 * Props:
 *   text     — the string to type out (italic IM Fell quote text)
 *   label    — the small header bar text (defaults to a path-style label)
 *   speed    — ms per character (default 28)
 *   testId   — optional data-testid on the outer wrapper
 */
export default function TerminalQuote({
  text,
  label = "SOMA://WISDOM.LOG",
  speed = 28,
  testId,
}) {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);
  const [shown, setShown] = useState("");
  const [done, setDone] = useState(false);

  // observe entry into viewport
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStarted(true);
          obs.disconnect();
        }
      },
      { threshold: 0.4 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  // typewriter effect
  useEffect(() => {
    if (!started) return;
    let i = 0;
    const tick = () => {
      i += 1;
      setShown(text.slice(0, i));
      if (i >= text.length) {
        setDone(true);
        return;
      }
      const ch = text[i - 1];
      // pause longer on sentence breaks for a natural cadence
      const delay = ".!?".includes(ch) ? speed * 8 : ",;:—".includes(ch) ? speed * 3 : speed;
      timer = setTimeout(tick, delay);
    };
    let timer = setTimeout(tick, 380); // brief delay before typing starts
    return () => clearTimeout(timer);
  }, [started, text, speed]);

  return (
    <div
      ref={ref}
      data-testid={testId}
      className="terminal-frame"
      style={{ margin: "8px 0 8px", overflow: "hidden" }}
    >
      <div className="terminal-bar">
        <span>{label}</span>
        <span style={{ letterSpacing: "0.2em" }}>● ● ●</span>
      </div>
      <div className="terminal-corners" style={{ position: "relative", padding: "26px 22px 22px", minHeight: 80 }}>
        <span className="tc tc-tl">┌</span>
        <span className="tc tc-tr">┐</span>
        <span className="tc tc-bl">└</span>
        <span className="tc tc-br">┘</span>

        <p
          style={{
            fontFamily: "'IM Fell DW Pica', serif",
            fontStyle: "italic",
            fontSize: 17,
            lineHeight: 1.65,
            color: "var(--terracotta-soft)",
            margin: 0,
            textAlign: "center",
            position: "relative",
            zIndex: 1,
            textShadow: "0 0 14px rgba(143, 196, 224, 0.35)",
          }}
        >
          {shown}
          <span
            aria-hidden
            style={{
              display: "inline-block",
              width: "0.55em",
              marginLeft: 2,
              transform: "translateY(2px)",
              color: "var(--cream)",
              animation: "blink-cursor 0.9s steps(2, start) infinite",
              opacity: done ? 0.55 : 1,
            }}
          >
            ▍
          </span>
        </p>
      </div>
      <style>{`
        @keyframes blink-cursor {
          0%, 50% { opacity: 1; }
          50.01%, 100% { opacity: 0; }
        }
      `}</style>
    </div>
  );
}
